import { useCountryStore } from "~~/stores/country";

/**
 * Expected length of the local (country code less) phone number: the configured phone
 * length, or the placeholder count of the phone format as a fallback.
 *
 * In a phone format `9` is the digit placeholder and everything else is a literal
 * ("99 999 9999", "(99) 999 9999"), so only the placeholders are counted - a literal
 * digit in the format would otherwise inflate the length.
 *
 * It only tells how a number is meant to be read, a number of another length is still
 * accepted (see `isLocalPhone`).
 */
export function localPhoneLength() {
  const countryStore = useCountryStore();
  return (
    Number(countryStore.phoneLength) ||
    (countryStore.phoneMask ?? "").replaceAll(/[^9]/g, "").length
  );
}

/** The configured country code in digits, without the `+` or the `00` it may be written with. */
function localCountryCode() {
  const countryStore = useCountryStore();
  return (countryStore.phoneCountryCode ?? "").toString().replaceAll(/\D/g, "");
}

/**
 * The trunk prefixes a local number may be written with (06, 1, ...), longest first.
 *
 * The international forms (0036, +36) are not trunk prefixes: a number written that way
 * is recognised by its international marker, so they are dropped here to keep them from
 * being cut off the front of a national number.
 */
function localTrunkPrefixes() {
  const countryStore = useCountryStore();
  return [
    ...new Set(
      countryStore.phonePrefixes.map((prefix) =>
        (prefix ?? "").toString().replaceAll(/\D/g, "")
      )
    ),
  ]
    .filter((prefix) => prefix && !prefix.startsWith("00"))
    .sort((first, second) => second.length - first.length);
}

/**
 * The local (country code less) part of a number written in the national format.
 *
 * Every reading the configuration allows is collected and the one that is a whole local
 * number wins, so a prefix is only cut off when what remains is a complete number and a
 * number that is still being typed keeps the digits it has so far.
 */
function nationalToLocal(digits) {
  const countryCode = localCountryCode();
  const length = localPhoneLength();

  const prefixes = localTrunkPrefixes();
  const carriesPrefix = prefixes.some((prefix) => digits.startsWith(prefix));
  const trimmed = prefixes
    .filter((prefix) => digits.startsWith(prefix))
    .map((prefix) => digits.slice(prefix.length));

  if (!length) {
    return trimmed[0] ?? digits;
  }

  // a whole local number is the strongest signal there is - a number that carries a
  // trunk prefix is never one of them, so a trunk form that happens to be as long as
  // the format (the german 030 12345678) is not mistaken for a local number
  const whole = [...(carriesPrefix ? [] : [digits]), ...trimmed].find(
    (local) => local.length === length
  );
  if (whole !== undefined) {
    return whole;
  }

  // the country code is only cut off when the number is exactly a country code and a
  // local number, otherwise a local number that starts with the country code (the 36
  // area code under the 36 country code) would lose its first digits
  if (
    countryCode &&
    digits.startsWith(countryCode) &&
    digits.length === countryCode.length + length
  ) {
    return digits.slice(countryCode.length);
  }

  // a configured prefix is authoritative even when what remains is shorter than the
  // format, because landlines are often shorter than the mobile numbers it describes
  if (trimmed.length) {
    return trimmed[0];
  }

  // nothing here says how to read the number: it is still being typed, or it carries a
  // prefix that is not configured. It is kept as it is rather than cut down to length,
  // so that it fails the completeness check instead of quietly becoming a different,
  // valid looking number of the configured country.
  return digits;
}

/**
 * The international number the backend expects: digits only, no `+` and no separators.
 *
 * A number that carries an international marker (`+` or a leading `00`) already names
 * its own country, so it is taken as it is and a number of any country survives
 * unchanged. Anything else is read as a national number of the configured country.
 */
export function toInternationalPhone(value) {
  const raw = (value ?? "").toString().trim();
  const digits = raw.replaceAll(/\D/g, "");
  if (!digits) {
    return "";
  }

  if (raw.startsWith("+")) {
    return digits;
  }
  if (digits.startsWith("00")) {
    return digits.slice(2);
  }

  return `${localCountryCode()}${nationalToLocal(digits)}`;
}

/**
 * Turns any phone number format into the local number the inputs and the masks work with.
 *
 * Browsers autofill the number the way the user has saved it (+36 30 123 4567,
 * 0036 30 123 4567, 06 30 123 4567, 30/123-4567), so the international marker, the
 * country code and the trunk prefix are cut off until the local number remains.
 */
export function toLocalPhone(value) {
  const countryCode = localCountryCode();
  const international = toInternationalPhone(value);

  // a number of another country has no local form here, it is left to the caller
  return countryCode && international.startsWith(countryCode)
    ? international.slice(countryCode.length)
    : international;
}

/** The number in the country code + local number format the backend expects. */
export function toFullPhone(value) {
  return toInternationalPhone(value);
}

/** The most digits a phone number can have together with its country code (E.164). */
const MAX_PHONE_DIGITS = 15;

/**
 * Groups the digits of a local number by the phone format (301234567 -> 30 123 4567).
 *
 * The format is a pattern, not a length: a shorter number stops where its digits run out,
 * and the digits of a longer one are appended to its last group instead of being dropped,
 * because the numbers of a country are not all the same length (the austrian ones vary).
 */
export function formatLocalPhone(local) {
  const format = useCountryStore().phoneMask ?? "";
  const digits = (local ?? "").toString().replaceAll(/\D/g, "");

  let formatted = "";
  let next = 0;
  for (const char of format) {
    if (next === digits.length) {
      break;
    }
    if (char === "9") {
      formatted += digits[next++];
    } else if (!/\d/.test(char)) {
      // a literal digit of the format is left out, writing it would change the number
      formatted += char;
    }
  }

  return formatted + digits.slice(next);
}

/**
 * A phone input's value with its local number grouped by the phone format.
 *
 * Only a number written as a local one is grouped. One written with its country code or a
 * trunk prefix (+36 30 123 4567, 06 30 123 4567) is left as it is, because the format
 * describes the local number only and would split it in the wrong places.
 */
export function formatPhoneInput(value) {
  const raw = (value ?? "").toString();
  const digits = raw.replaceAll(/\D/g, "");

  if (!digits || raw.trim().startsWith("+") || toLocalPhone(raw) !== digits) {
    return raw;
  }

  return formatLocalPhone(digits);
}

/**
 * The phone format with room for the digits a number may have beyond it, up to the most a
 * phone number can have - for masks that only take as many digits as they have placeholders.
 */
export function overflowingPhoneFormat() {
  const format = useCountryStore().phoneMask ?? "";
  const room =
    MAX_PHONE_DIGITS -
    localCountryCode().length -
    format.replaceAll(/[^9]/g, "").length;

  return `${format}${"9".repeat(Math.max(room, 0))}`;
}

/**
 * Whether the country the number is checked against is known at all.
 *
 * The country is loaded per financier, so a page that is reached without one has nothing
 * to check against: the number is let through instead of turning a missing configuration
 * into a login that rejects every number.
 */
export function isCountryPhoneConfigured() {
  return Boolean(localCountryCode());
}

/**
 * Whether the value is a phone number of the configured country.
 *
 * The length of the number is not checked, because the numbers of a country are not all
 * the same length (the austrian ones vary), so any number that has a local part will do.
 *
 * A number of another country never satisfies this, so a caller can reject it instead of
 * sending it on as a number that was silently reshaped into the configured country.
 */
export function isLocalPhone(value) {
  const countryCode = localCountryCode();
  const international = toInternationalPhone(value);

  return (
    Boolean(countryCode) &&
    international.startsWith(countryCode) &&
    international.length > countryCode.length
  );
}

/**
 * Whether the value is a phone number of another country.
 *
 * A country code that is still being typed (+3 on its way to +36) is not a foreign one
 * yet, only one that can no longer become the configured country code.
 */
export function isForeignPhone(value) {
  const countryCode = localCountryCode();
  const international = toInternationalPhone(value);

  if (!countryCode || !international) {
    return false;
  }

  return (
    !international.startsWith(countryCode) &&
    !countryCode.startsWith(international)
  );
}

/**
 * Normalizes autofilled and pasted values before a masked input handles them, because
 * the mask keeps the first digits of the value and drops everything that does not fit.
 *
 * Bind it on a wrapping element with `@input.capture`, so it runs before the mask itself.
 * Set `withCountryCode` when the country code is part of the field's own mask.
 *
 * Returns whether the value was kept: a number of another country is refused, because
 * the mask would cut it down to the configured length and it would reach the backend as
 * a number of the configured country.
 */
export function normalizePhoneInput(event, { withCountryCode = false } = {}) {
  const input = event.target;
  if (typeof input?.value !== "string") {
    return true;
  }

  if (isForeignPhone(input.value)) {
    input.value = "";
    return false;
  }

  const expected = withCountryCode
    ? toInternationalPhone(input.value)
    : toLocalPhone(input.value);

  // while typing there is nothing to trim, the value must be left to the mask
  if (input.value.replaceAll(/\D/g, "") === expected) {
    return true;
  }

  input.value = withCountryCode ? `+${expected}` : expected;
  return true;
}
